import React, { useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { useWebRTC } from '../hooks/useWebRTC';
import { useClassTimer } from '../hooks/useClassTimer';
import VideoGrid from '../components/video/VideoGrid';
import ControlBar from '../components/video/ControlBar';
import ChatPanel from '../components/chat/ChatPanel';
import { saveRecordingAPI } from '../services/api';

const LiveClassPage = () => {
  const { roomCode } = useParams();
  const { user, addRecording } = useContext(AppContext);

  const handleRecordingComplete = useCallback(
    async ({ blob, url, durationSeconds }) => {
      // In production, upload blob to PHP and store file path.
      // For now, we only store metadata + (optionally) a temporary URL.
      const recMeta = {
        user_id: user.id,
        room_code: roomCode,
        title: `Class with Room ${roomCode}`,
        duration: durationSeconds,
        video_url: '', // upload later, keep blank
      };

      try {
        const saved = await saveRecordingAPI(recMeta);
        // merge server fields + ephemeral client URL
        addRecording({ ...saved, video_url: url });
        alert('Recording metadata saved successfully via PHP API');
      } catch (err) {
        console.error(err);
        alert('Error saving recording: ' + err.message);
      }
    },
    [addRecording, roomCode, user]
  );

  const {
    localVideoRef,
    participants,
    joinTime,
    isCameraOn,
    isMicOn,
    isScreenSharing,
    remoteStreams,
    toggleCamera,
    toggleMic,
    toggleScreenShare,
    startRecording,
    stopRecording,
  } = useWebRTC({
    roomCode,
    user,
    onRecordingComplete: handleRecordingComplete,
  });

  const timer = useClassTimer(joinTime);
  const joinLink = `${window.location.origin}/live-class/${roomCode}`;

  const copyJoinLink = () => {
    navigator.clipboard.writeText(joinLink).then(
      () => alert('Join link copied to clipboard'),
      () => alert('Failed to copy link')
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h4 className="mb-0">Live Class Room: {roomCode}</h4>
        <div className="d-flex align-items-center">
          <small className="me-2 text-muted">Share this link with students:</small>
          <input
            type="text"
            className="form-control form-control-sm me-2"
            style={{ width: 260 }}
            readOnly
            value={joinLink}
          />
          <button className="btn btn-sm btn-outline-primary" onClick={copyJoinLink}>
            Copy
          </button>
        </div>
      </div>
      <div className="live-class-container">
        <div className="live-class-main">
          <VideoGrid
            localVideoRef={localVideoRef}
            participants={participants}
            remoteStreams={remoteStreams}
          />
          <ControlBar
            isCameraOn={isCameraOn}
            isMicOn={isMicOn}
            isScreenSharing={isScreenSharing}
            onToggleCamera={toggleCamera}
            onToggleMic={toggleMic}
            onToggleScreenShare={toggleScreenShare}
            onStartRecording={startRecording}
            onStopRecording={stopRecording}
            classTimer={timer}
          />
        </div>
        <div className="live-class-side">
          <ChatPanel roomCode={roomCode} user={user} />
        </div>
      </div>
    </div>
  );
};

export default LiveClassPage;
